import {
  SubprojectBuildingDetails,
  TechnologyParameter,
} from '@/stores/SubprojectStore';
import { useEffect, useState } from 'react';
import { Tab } from './types';
import {
  categorySelectionsMatch,
  createEmptyBuildingAlternative,
  createEmptySubprojectTechnology,
  updateTechParams,
} from './utils';

export const useDecarbTechSelection = <
  T extends {
    id: number;
    capitalCostPerUnit: string;
    energyUsePerYr: string;
    unitsNeededPerSqft: string;
    limit: number;
    energySink?: string;
    relativeEnergyDemand?: 1 | 2 | 3 | 4;
  },
>(
  technologyKey: string,
  fetchedSubproject?: SubprojectBuildingDetails,
  items?: T[],
) => {
  const [currentTab, setCurrentTab] = useState<Tab>('conventionalAlternative');
  const [subproject, setSubproject] = useState<SubprojectBuildingDetails>();
  // The copy last adopted from the server. Every save invalidates the
  // subproject query, so the refetch lands here with freshly computed stored
  // costs; until it does, an edit on screen makes those costs stale.
  const [syncedSubproject, setSyncedSubproject] =
    useState<SubprojectBuildingDetails>();

  useEffect(() => {
    if (!fetchedSubproject) {
      return;
    }

    const updatedSubproject = structuredClone(fetchedSubproject);

    if (!updatedSubproject.conventionalAlternative) {
      updatedSubproject.conventionalAlternative =
        createEmptyBuildingAlternative();
    }
    if (
      !updatedSubproject.conventionalAlternative.technologyParams[technologyKey]
    ) {
      updatedSubproject.conventionalAlternative.technologyParams[
        technologyKey
      ] = createEmptySubprojectTechnology(technologyKey);
    }
    if (!updatedSubproject.reducedCarbonAlternative) {
      updatedSubproject.reducedCarbonAlternative =
        createEmptyBuildingAlternative();
    }
    if (
      !updatedSubproject.reducedCarbonAlternative.technologyParams[
        technologyKey
      ]
    ) {
      updatedSubproject.reducedCarbonAlternative.technologyParams[
        technologyKey
      ] = createEmptySubprojectTechnology(technologyKey);
    }
    if (!updatedSubproject.zeroCarbonAlternative) {
      updatedSubproject.zeroCarbonAlternative =
        createEmptyBuildingAlternative();
    }
    if (
      !updatedSubproject.zeroCarbonAlternative.technologyParams[technologyKey]
    ) {
      updatedSubproject.zeroCarbonAlternative.technologyParams[technologyKey] =
        createEmptySubprojectTechnology(technologyKey);
    }

    // ALWAYS update when fetchedSubproject changes to fix data persistence bug
    setSubproject(updatedSubproject);
    setSyncedSubproject(updatedSubproject);
  }, [fetchedSubproject, technologyKey]);

  const technologyParams = subproject?.[currentTab]?.technologyParams?.[
    technologyKey
  ]?.params
    ? subproject?.[currentTab]?.technologyParams?.[technologyKey]?.params
    : [];

  // Filter technologies based on the current alternative
  const filterTechnologyByAlternative = (item: T): boolean => {
    // Conventional: All technologies available
    if (currentTab === 'conventionalAlternative') {
      return true;
    }

    // Low Carbon: No gas (Natural Gas, Electricity/Gas, etc.)
    if (currentTab === 'reducedCarbonAlternative') {
      return !item.energySink?.includes('Gas');
    }

    // Net Zero: No gas AND only low-energy electricity (≤2 icons) or other energy sources
    if (currentTab === 'zeroCarbonAlternative') {
      if (item.energySink?.includes('Gas')) {
        return false;
      }
      if (
        item.energySink === 'Electricity' &&
        item.relativeEnergyDemand &&
        item.relativeEnergyDemand > 2
      ) {
        return false;
      }
      return true;
    }

    return true;
  };

  const availableItems =
    items && technologyParams
      ? items.filter(
          (item) =>
            !technologyParams.find(
              (param) => param.techId === String(item.id),
            ) && filterTechnologyByAlternative(item),
        )
      : [];

  const selectedItems =
    items && technologyParams ? mapParamsToItems(technologyParams) : [];

  function mapParamsToItems(params?: TechnologyParameter[]) {
    if (!params || !items) {
      return [];
    }

    return params
      .map((param) => {
        const foundItem = items.find(
          (item) => String(item.id) === param.techId,
        );
        if (!foundItem) return null;
        return { ...foundItem, count: param.units };
      })
      .filter((item) => item !== null);
  }

  const copyItemSelectionFromTab = (tab: Tab) => {
    if (tab === 'conventionalAlternative') {
      return;
    }

    let tabToCopyFrom: Tab = 'conventionalAlternative';

    if (tab === 'zeroCarbonAlternative') {
      tabToCopyFrom = 'reducedCarbonAlternative';
    }

    // check if from tab has a nonempty list of technology selections
    setSubproject((prev) => {
      if (!prev?.[tabToCopyFrom]?.technologyParams?.[technologyKey]?.params) {
        return prev;
      }

      if (!prev?.[tab]?.technologyParams?.[technologyKey]?.params) {
        return prev;
      }

      const paramsToCopy =
        prev[tabToCopyFrom].technologyParams[technologyKey].params;

      // Filter params - only copy technologies that are allowed in the target alternative
      const filteredParams = paramsToCopy.filter((param) => {
        // Find the actual technology item to check its properties
        const item = items?.find((i) => String(i.id) === param.techId);
        if (!item) return false;

        // Apply the same filtering logic based on target tab (where we're copying TO)
        // Low Carbon: No gas (Natural Gas, Electricity/Gas, etc.)
        if (tab === 'reducedCarbonAlternative') {
          return !item.energySink?.includes('Gas');
        }

        // Net Zero: No gas AND only low-energy electricity or other sources
        if (tab === 'zeroCarbonAlternative') {
          if (item.energySink?.includes('Gas')) {
            return false;
          }
          if (
            item.energySink === 'Electricity' &&
            item.relativeEnergyDemand &&
            item.relativeEnergyDemand > 2
          ) {
            return false;
          }
          return true;
        }

        return true;
      });

      const excludedCount = paramsToCopy.length - filteredParams.length;
      if (excludedCount > 0) {
        console.log(
          `[Copy] ${excludedCount} item(s) excluded during copy to ${tab} for ${technologyKey}`,
        );
      }

      return updateTechParams(prev, tab, technologyKey, () => [
        ...filteredParams,
      ]);
    });
  };

  const handleAdd = (item: T) => {
    setSubproject((prev) => {
      if (!prev?.[currentTab]?.technologyParams?.[technologyKey]?.params) {
        return prev;
      }

      const newItem: TechnologyParameter = {
        techId: String(item.id),
        units: item.limit,
      };

      return updateTechParams(prev, currentTab, technologyKey, (params) => [
        ...params,
        newItem,
      ]);
    });
  };

  const handleRemove = (item: T & { count: number }) => {
    setSubproject((prev) => {
      if (!prev?.[currentTab]?.technologyParams?.[technologyKey]?.params) {
        console.warn('handleRemove: Missing technology params structure', {
          currentTab,
          technologyKey,
        });
        return prev;
      }

      return updateTechParams(prev, currentTab, technologyKey, (params) => {
        const newTechParams = params.filter(
          (prevItem) => prevItem.techId !== String(item.id),
        );

        console.log('handleRemove:', {
          itemId: item.id,
          itemIdAsString: String(item.id),
          before: params.length,
          after: newTechParams.length,
          removed: params.length - newTechParams.length,
        });

        return newTechParams;
      });
    });
  };

  const handleChangeItemCount = (
    item: T & { count: number },
    count: number,
  ) => {
    const newCount = Number(count);
    // Validate: must be positive number, no zero allowed
    if (isNaN(newCount) || newCount === Infinity || newCount <= 0) {
      return;
    }

    setSubproject((prev) => {
      if (!prev?.[currentTab]?.technologyParams?.[technologyKey]?.params) {
        return prev;
      }

      return updateTechParams(prev, currentTab, technologyKey, (params) =>
        params.map((param) =>
          param.techId === String(item.id)
            ? { techId: String(item.id), units: newCount }
            : param,
        ),
      );
    });
  };

  // True while the selections on screen differ from what the server last
  // computed costs for: the stored figures are out of date and must not be
  // shown (see storedCategoryCapitalCost).
  const costsArePending = !categorySelectionsMatch(
    subproject,
    syncedSubproject,
    technologyKey,
  );

  return {
    availableItems,
    costsArePending,
    currentTab,
    selectedItems,
    subproject,
    copyItemSelectionFromTab,
    handleAdd,
    handleChangeItemCount,
    handleRemove,
    setCurrentTab,
  };
};
